import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type InputHTMLAttributes,
} from "react";
import Search from "@mui/icons-material/Search";
import {
  Alert,
  Autocomplete,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox as MuiCheckbox,
  TextField as MuiTextField,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { debug as debugFn } from "debug";
import { useField } from "formik";
import { useTranslation } from "react-i18next";

import type {
  TranslationKeyAndOptions,
  UserUnbranded,
} from "@thalamos/vision-common";
import { tk, translate } from "@thalamos/vision-common";

import { createUserSearch } from "../../../api/user/createUserSearch.js";
import { useUser } from "../../../Context/UserContext.js";
import { FormError } from "../FormError/FormError.js";
import { FormLabel } from "../FormLabel/FormLabel.js";
import { TextField } from "../TextField/TextField.js";
import { useOfficerLookupContext } from "./OfficerLookupContext.js";

const debug = debugFn("vision-frontend:OfficerLookup");

// A JSX element is an immutable description, not a component instance: React
// mounts a separate icon wherever this is rendered. One shared element keeps
// `startAdornment`'s identity stable; MUI's InputBase compares it in an effect
// that sets FormControl state, so a fresh element per render fires on every
// keystroke.
const SEARCH_ADORNMENT = <Search fontSize="medium" color="primary" />;

export type OfficerLookupProps = {
  fieldName: string;
  label?: TranslationKeyAndOptions;
  placeholder?: TranslationKeyAndOptions;
  autoFocus?: boolean;
};

type TextFieldOutput =
  { value: string | null; isUnknown?: boolean } | "" | undefined | null;

/**
 * A dual-mode field for capturing the shoulder/warrant number of another officer.
 *
 * - When `officerLookupEnabled` is true in the org config, renders a searchable
 *   typeahead (name / email / warrant number). The user must select an officer and
 *   tick a confirmation checkbox before the value is committed to Formik.
 * - When `officerLookupEnabled` is false (or absent), renders as a plain text input
 *   — identical to the previous `textbox` behaviour — so unoptioned orgs are unaffected.
 */
export function OfficerLookup({
  fieldName,
  label,
  placeholder,
  autoFocus,
}: OfficerLookupProps) {
  const { user } = useUser();

  const officerLookupEnabled =
    user?.sessionOrganisationConfiguration?.officerLookupEnabled ?? false;

  // When lookup is disabled, fall back to the standard text input
  if (!officerLookupEnabled) {
    return (
      <TextField
        fieldName={fieldName}
        label={label}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
    );
  }

  return (
    <OfficerLookupTypeahead
      fieldName={fieldName}
      label={label}
      placeholder={placeholder}
      autoFocus={autoFocus}
      organisationId={user?.sessionContext?.organisationId}
    />
  );
}

// ---------------------------------------------------------------------------
// Internal typeahead — only mounted when officerLookupEnabled is true
// ---------------------------------------------------------------------------

type LookupState = "idle" | "loading" | "found" | "not-found";

type OfficerLookupTypeaheadProps = OfficerLookupProps & {
  organisationId: string | undefined;
};

function OfficerLookupTypeahead({
  fieldName,
  label,
  placeholder,
  autoFocus,
  organisationId,
}: OfficerLookupTypeaheadProps) {
  const theme = useTheme();

  // Scope officer search to the current user's organisation, unless overridden
  // by OfficerLookupContext (e.g. in Storybook with mock data).
  const { searchUsers: contextSearchUsers } = useOfficerLookupContext();
  const searchUsers = useMemo(
    () => contextSearchUsers ?? createUserSearch(organisationId),
    [contextSearchUsers, organisationId],
  );

  const [_field, _meta, helpers] = useField<TextFieldOutput>(fieldName);
  // Store helpers in a ref so callbacks don't depend on its identity.
  const helpersRef = useRef(helpers);
  helpersRef.current = helpers;

  // The currently displayed text in the typeahead input
  const [inputValue, setInputValue] = useState<string>("");
  // Autocomplete suggestions from the last search
  const [options, setOptions] = useState<UserUnbranded[]>([]);
  // Whether the confirmation checkbox is ticked
  const [confirmed, setConfirmed] = useState<boolean>(false);
  // The debounce timer ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Incrementing counter to discard stale search results (race-condition guard).
  const requestIdRef = useRef(0);
  // Reactive lookup state — drives the details panel
  const [lookupState, setLookupState] = useState<LookupState>("idle");
  // The officer found when the typed number exactly matches a shoulder number
  const [matchedOfficer, setMatchedOfficer] = useState<UserUnbranded | null>(
    null,
  );

  // ---------------------------------------------------------------------------
  // On first mount, if Formik already holds a confirmed value (e.g. the user is
  // revisiting a partially completed form), restore the component state so the
  // details panel and pre-ticked checkbox are shown immediately.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const existing = _field.value;
    if (
      existing != null &&
      typeof existing === "object" &&
      "value" in existing &&
      typeof existing.value === "string" &&
      existing.value.length > 0
    ) {
      const savedNumber = existing.value;
      setInputValue(savedNumber);
      setConfirmed(true);
      setLookupState("loading");

      void (async () => {
        const results = await searchUsers(savedNumber, 10, 0).unwrapOr([]);
        setOptions(results);
        const exactMatch = results.find(
          (r) => r.shoulderNumber?.toLowerCase() === savedNumber.toLowerCase(),
        );
        if (exactMatch) {
          setMatchedOfficer(exactMatch);
          setLookupState("found");
        } else {
          setLookupState("not-found");
        }
      })();
    }
    // Only runs once on mount — searchUsers and _field.value are intentionally
    // excluded from deps to avoid re-running on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up the debounce timer on unmount to prevent setState on an unmounted component.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Every change to the input drives a fresh lookup.  When the input is empty
  // we reset to idle.  Any non-empty value starts a debounced search; after
  // the results arrive we check for an exact shoulder-number match.
  const handleInputChange = useCallback(
    (rawValue: string | null) => {
      const value = rawValue ?? "";
      setInputValue(value);
      setConfirmed(false);
      void helpersRef.current.setValue("");

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (value.length === 0) {
        setLookupState("idle");
        setMatchedOfficer(null);
        setOptions([]);
        return;
      }

      setLookupState("loading");
      setMatchedOfficer(null);

      debounceRef.current = setTimeout(() => {
        const thisRequest = ++requestIdRef.current;
        void (async () => {
          const results = await searchUsers(value, 10, 0).unwrapOr([]);

          // Discard stale results — a newer request has already been issued.
          if (thisRequest !== requestIdRef.current) return;

          setOptions(results);

          const exactMatch = results.find(
            (r) => r.shoulderNumber?.toLowerCase() === value.toLowerCase(),
          );

          if (exactMatch) {
            setMatchedOfficer(exactMatch);
            setLookupState("found");
          } else {
            setLookupState("not-found");
          }
        })();
      }, 300);
    },
    [searchUsers],
  );

  const handleConfirmChange = useCallback(
    (checked: boolean) => {
      setConfirmed(checked);
      if (checked) {
        // When found, commit the canonical shoulder number.  When not-found
        // (matchedOfficer is null) commit whatever the user typed — this is
        // the false-negative / unregistered-number path where the user has
        // confirmed the typed value really is a valid shoulder number.
        //
        // Guard: if for any reason the matched officer has no shoulderNumber,
        // fall through to the typed inputValue rather than storing an email.
        const shoulder = matchedOfficer?.shoulderNumber;
        void helpersRef.current.setValue({
          value: shoulder ?? inputValue,
        });
      } else {
        void helpersRef.current.setValue("");
      }
    },
    [matchedOfficer, inputValue],
  );

  // Client-side filter mirrors InitiateTransfer: match on email, name, or shoulder number
  const filterOptionsFn = useCallback(
    (
      opts: UserUnbranded[],
      state: {
        inputValue: string;
        getOptionLabel: (option: UserUnbranded) => string;
      },
    ): UserUnbranded[] => {
      const query = state.inputValue.toLowerCase();
      if (!query) return opts;
      return opts.filter(
        (opt) =>
          opt.email.toLowerCase().includes(query) ||
          opt.name.toLowerCase().includes(query) ||
          (opt.shoulderNumber?.toLowerCase().includes(query) ?? false),
      );
    },
    [],
  );

  // Two-row option renderer matching the InitiateTransfer pattern
  const renderOptionFn = useCallback(
    (props: HTMLAttributes<HTMLLIElement>, option: UserUnbranded) => {
      const showShoulderNumber =
        option.shoulderNumber &&
        !option.name
          .toLowerCase()
          .includes(option.shoulderNumber.toLowerCase());

      const subLabel = showShoulderNumber
        ? `${option.name} (${option.shoulderNumber ?? ""})`
        : option.name;

      return (
        <li {...props} key={option.email}>
          <Box>
            <Typography
              component="div"
              sx={{ fontWeight: "bold", fontSize: "0.875rem" }}
            >
              {option.email}
            </Typography>
            <Typography
              component="div"
              sx={{ fontSize: "0.75rem", color: "text.secondary" }}
            >
              {subLabel}
            </Typography>
          </Box>
        </li>
      );
    },
    [],
  );

  const getOptionLabel = useCallback(
    (option: string | UserUnbranded) =>
      typeof option === "string"
        ? option
        : (option.shoulderNumber ?? option.email),
    [],
  );

  const { t } = useTranslation();

  // The error could either come from Formik ("Invalid input") or from the
  // schema ({ value: "string too short" }).  Normalise and translate once.
  const fieldError = useMemo(
    () => {
      const value =
        typeof _meta.error === "string"
          ? _meta.error
          : typeof _meta.error === "object"
            ? (_meta.error as { value: string } | undefined)?.value
            : undefined;

      return value === undefined ? undefined : t(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(_meta.error)],
  );

  const resolvedPlaceholder = placeholder ?? {
    translationKey: tk("formBuilder.officerLookup.placeholder"),
  };
  const placeholderText = translate(t, resolvedPlaceholder);
  const ariaLabel = label ? translate(t, label) : placeholderText;

  return (
    <Box sx={{ paddingBottom: theme.spacing(3) }}>
      <FormControl fullWidth data-testid={`formField-${fieldName}`}>
        {label && (
          <Box sx={{ marginBottom: "0.5rem" }}>
            <FormLabel {...label} />
          </Box>
        )}

        {/* Direct Autocomplete — no form-builder TextField, so the display
            input is purely React state and never writes to Formik. */}
        <Autocomplete<UserUnbranded, false, true, true>
          freeSolo
          disableClearable
          options={lookupState === "found" ? [] : options}
          getOptionLabel={getOptionLabel}
          filterOptions={filterOptionsFn}
          renderOption={renderOptionFn}
          inputValue={inputValue}
          onChange={(_e, value) => {
            // When the user selects from the dropdown, short-circuit directly
            // to the "found" state — no need to fire another search.
            if (typeof value !== "string" && value != null) {
              if (debounceRef.current) clearTimeout(debounceRef.current);

              if (value.shoulderNumber) {
                // Officer has a shoulder number — show "found" state.
                setInputValue(value.shoulderNumber);
                setMatchedOfficer(value);
                setLookupState("found");
              } else {
                // Officer exists but has no shoulder number — treat as
                // "not found" so the user must manually type and confirm
                // a shoulder number instead of committing an email address
                // or other non-shoulder-number text.
                setMatchedOfficer(null);
                setLookupState("not-found");
              }

              setConfirmed(false);
              void helpersRef.current.setValue("");
            }
          }}
          onInputChange={(_e, v, reason) => {
            // Skip the input-change fired by MUI after an option selection —
            // that case is already handled by onChange above.
            if (reason === "reset") return;
            handleInputChange(v);
          }}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              placeholder={placeholderText}
              autoFocus={autoFocus}
              slotProps={{
                input: {
                  ...params.InputProps,
                  autoComplete: "off",
                  type: "search",
                  startAdornment: SEARCH_ADORNMENT,
                },
                htmlInput: {
                  ...params.inputProps,
                  "aria-label": ariaLabel,
                },
              }}
            />
          )}
        />
      </FormControl>

      {debug.enabled && (
        <>
          <Divider />
          <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
            {"field.value : "}
            {JSON.stringify(_field.value)}
          </Typography>
          <Divider />
        </>
      )}

      {/* When idle and touched, show the error above (no panel to anchor it to) */}
      {_meta.touched && fieldError && lookupState === "idle" && (
        <FormError error={fieldError} />
      )}

      {lookupState !== "idle" && (
        <OfficerDetailsPanel
          variant={lookupState}
          officer={matchedOfficer ?? undefined}
          fieldName={fieldName}
          confirmed={confirmed}
          onConfirmChange={handleConfirmChange}
          error={_meta.touched && fieldError ? fieldError : undefined}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Unified details panel — same layout for loading / found / not-found states.
// Undefined officer fields render as skeleton placeholders, which keeps all
// three states visually consistent and easy to evolve together.
// ---------------------------------------------------------------------------

type OfficerDetailsPanelProps = {
  variant: "loading" | "found" | "not-found";
  /** Matched officer — defined only when variant is "found" */
  officer?: UserUnbranded;
  fieldName: string;
  confirmed: boolean;
  onConfirmChange: (checked: boolean) => void;
  /** Validation error to show adjacent to the checkbox after a failed submit attempt */
  error?: string;
};

function OfficerDetailsPanel({
  variant,
  officer,
  fieldName,
  confirmed,
  onConfirmChange,
  error,
}: OfficerDetailsPanelProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const isLoading = variant === "loading";
  const isNotFound = variant === "not-found";

  return (
    <Box
      data-testid={`officer-lookup-${variant}`}
      aria-busy={isLoading}
      sx={{
        mt: 2,
        p: 2,
        border: `1px solid ${isNotFound ? theme.palette.warning.main : theme.palette.divider}`,
        borderRadius: 1,
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Officer data rows — shown for loading (skeletons) and found (real values) */}
      {!isNotFound && (
        <>
          {(isLoading || officer?.shoulderNumber != null) &&
            (isLoading ? (
              <Typography variant="body2">
                <Skeleton />
              </Typography>
            ) : (
              <Typography variant="body2">
                <strong>
                  {t("formBuilder.officerLookup.shoulderNumber")}:{" "}
                </strong>
                {officer?.shoulderNumber}
              </Typography>
            ))}

          {isLoading ? (
            <Typography variant="body2">
              <Skeleton />
            </Typography>
          ) : (
            <Typography variant="body2">
              <strong>{t("formBuilder.officerLookup.officerName")}: </strong>
              {officer?.name}
            </Typography>
          )}

          {isLoading ? (
            <Typography variant="body2">
              <Skeleton />
            </Typography>
          ) : (
            <Typography variant="body2">
              <strong>{t("formBuilder.officerLookup.email")}: </strong>
              {officer?.email}
            </Typography>
          )}
        </>
      )}

      {/* Warning alert — shown only when no officer was found */}
      {isNotFound && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2" component="span" fontWeight="bold">
            {t("formBuilder.officerLookup.notFoundHeading")}
          </Typography>
          <Typography variant="body2">
            {t("formBuilder.officerLookup.notFoundDetail")}
          </Typography>
        </Alert>
      )}

      {/* Confirmation checkbox — always present; disabled with skeleton label while loading */}
      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <MuiCheckbox
            checked={confirmed}
            disabled={isLoading}
            onChange={(e) => {
              onConfirmChange(e.target.checked);
            }}
            slotProps={{
              input: {
                "data-testid": `${fieldName}-confirm-checkbox`,
              } as InputHTMLAttributes<HTMLInputElement>,
            }}
          />
        }
        label={
          isLoading ? (
            <Typography variant="body2">
              <Skeleton width={160} />
            </Typography>
          ) : (
            <Typography variant="body2">
              {isNotFound
                ? t("formBuilder.officerLookup.confirmationLabelNotFound")
                : t("formBuilder.officerLookup.confirmationLabel")}
            </Typography>
          )
        }
      />

      {error && <FormError error={error} />}
    </Box>
  );
}
